import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, FileText, ArrowRight, Trash2, Archive, Eye, Download, Pencil, Check, X, Send, Copy, Link2, ArrowUpDown, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      {...attributes}
    >
      {children(listeners)}
    </div>
  );
}

export default function Estimates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const showDeleted = searchParams.get('view') === 'deleted';
  
  const [estimates, setEstimates] = useState([]);
  const [deletedCount, setDeletedCount] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByCustomer, setSortByCustomer] = useState('none'); // 'none' | 'asc' | 'desc'
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [user, setUser] = useState(null);
  
  // Inline creation states
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '', company_name: '', email: '', phone: '', whatsapp: '', city: '', address: ''
  });

  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [newProductData, setNewProductData] = useState({
    name: '', description: '', category_id: '', unit: 'pcs', price: '', stock_quantity: ''
  });
  
  // Helper function to get default valid until date (1 month from today)
  function getDefaultValidUntil() {
    const today = new Date();
    const nextMonth = new Date(today.setMonth(today.getMonth() + 1));
    return nextMonth.toISOString().split('T')[0];
  }

  const [estimateForm, setEstimateForm] = useState({
    customer_id: '',
    estimate_date: new Date().toISOString().split('T')[0],
    valid_until: getDefaultValidUntil(),
    subject: '',
    notes: '',
    production_notes: '',
    bank_account: 'ndb',
    display_total_amounts: true,
    display_grand_total: true,
    discount: 0,
    discount_type: 'amount',
    items: [{ _uid: crypto.randomUUID(), product_id: '', product_name: '', description: '', category_id: '', size: '', unit: 'pcs', quantity: '', unit_price: '', display_amounts: true, offcut_size: '', offcut_rate: 0 }]
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchEstimates();
    fetchCustomers();
    fetchProducts();
    fetchCategories();
    if (!showDeleted) {
      fetchDeletedCount();
    }
  }, [showDeleted]);

  const fetchEstimates = async () => {
    try {
      const response = await api.get(`/estimates?include_deleted=${showDeleted}`);
      // When viewing deleted, filter to show only deleted items
      const filteredData = showDeleted 
        ? response.data.filter(e => e.deleted === true)
        : response.data;
      setEstimates(filteredData);
    } catch (error) {
      toast.error('Failed to fetch estimates');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedCount = async () => {
    try {
      const response = await api.get('/estimates?include_deleted=true');
      const deleted = response.data.filter(e => e.deleted === true);
      setDeletedCount(deleted.length);
    } catch (error) {
      console.error('Failed to fetch deleted count');
    }
  };

  const handleDeleteEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    try {
      await api.delete(`/estimates/${estimateId}`);
      toast.success('Estimate deleted successfully', {
        style: { background: '#10b981', color: 'white' }
      });
      fetchEstimates();
    } catch (error) {
      toast.error('Failed to delete estimate', {
        style: { background: '#ef4444', color: 'white' }
      });
    }
  };

  const handleRestoreEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to restore this estimate?')) return;
    try {
      await api.put(`/estimates/${estimateId}/restore`);
      toast.success('Estimate restored successfully', {
        style: { background: '#10b981', color: 'white' }
      });
      setEstimates(estimates.filter(e => e.id !== estimateId));
      setDeletedCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to restore estimate', {
        style: { background: '#ef4444', color: 'white' }
      });
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/product-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  // Inline customer creation function
  const handleAddNewCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/customers', newCustomerData);
      toast.success('Customer added successfully', { style: { background: '#10b981', color: 'white' } });
      setAddCustomerDialogOpen(false);
      setNewCustomerData({ name: '', company_name: '', email: '', phone: '', whatsapp: '', city: '', address: '' });
      await fetchCustomers();
      setEstimateForm({ ...estimateForm, customer_id: response.data.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add customer', { style: { background: '#ef4444', color: 'white' } });
    }
  };

  // Inline product creation function
  const handleAddItemToProducts = (item) => {
    setNewProductData({
      name: item.product_name,
      description: item.description || '',
      category_id: item.category_id || '',
      unit: item.unit || 'pcs',
      price: item.unit_price,
      stock_quantity: ''
    });
    setAddProductDialogOpen(true);
  };

  const handleAddNewProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post('/products', newProductData);
      toast.success('Product added successfully', { style: { background: '#10b981', color: 'white' } });
      setAddProductDialogOpen(false);
      setNewProductData({ name: '', description: '', category_id: '', unit: 'pcs', price: '', stock_quantity: '' });
      await fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product', { style: { background: '#ef4444', color: 'white' } });
    }
  };

  const handleCreateEstimate = async (e) => {
    e.preventDefault();
    try {
      if (editingEstimate) {
        await api.put(`/estimates/${editingEstimate.id}`, estimateForm);
        toast.success('Estimate updated successfully', { style: { background: '#10b981', color: 'white' } });
      } else {
        await api.post('/estimates', estimateForm);
        toast.success('Estimate created successfully', { style: { background: '#10b981', color: 'white' } });
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchEstimates();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingEstimate ? 'update' : 'create'} estimate`, { style: { background: '#ef4444', color: 'white' } });
    }
  };

  const handleEditEstimate = (estimate) => {
    setEditingEstimate(estimate);
    setEstimateForm({
      customer_id: estimate.customer_id,
      estimate_date: estimate.estimate_date,
      valid_until: estimate.valid_until,
      subject: estimate.subject || '',
      notes: estimate.notes || '',
      production_notes: estimate.production_notes || '',
      bank_account: estimate.bank_account || 'ndb',
      display_total_amounts: estimate.display_total_amounts !== undefined ? estimate.display_total_amounts : true,
      display_grand_total: estimate.display_grand_total !== undefined ? estimate.display_grand_total : true,
      discount: estimate.discount || 0,
      discount_type: estimate.discount_type || 'amount',
      items: estimate.items.map(item => ({
        _uid: item._uid || crypto.randomUUID(),
        product_id: item.product_id || '',
        product_name: item.product_name,
        description: item.description || '',
        category_id: item.category_id || '',
        size: item.size || '',
        unit: item.unit || 'pcs',
        quantity: item.quantity,
        unit_price: item.unit_price,
        display_amounts: item.display_amounts !== undefined ? item.display_amounts : true,
        offcut_size: item.offcut_size || '',
        offcut_rate: item.offcut_rate || 0
      }))
    });
    setCreateDialogOpen(true);
  };

  const resetForm = () => {
    setEstimateForm({
      customer_id: '',
      estimate_date: new Date().toISOString().split('T')[0],
      valid_until: getDefaultValidUntil(),
      subject: '',
      notes: '',
      production_notes: '',
      bank_account: 'ndb',
      display_total_amounts: true,
      display_grand_total: true,
      discount: 0,
      discount_type: 'amount',
      items: [{ _uid: crypto.randomUUID(), product_id: '', product_name: '', description: '', category_id: '', size: '', unit: 'pcs', quantity: '', unit_price: '', display_amounts: true, offcut_size: '', offcut_rate: 0 }]
    });
    setEditingEstimate(null);
  };

  const handleConvertToInvoice = async (estimateId) => {
    if (!window.confirm('Convert this estimate to an invoice?')) return;

    try {
      await api.post(`/estimates/${estimateId}/convert`);
      toast.success('Estimate converted to invoice successfully');
      fetchEstimates();
    } catch (error) {
      toast.error('Failed to convert estimate');
    }
  };

  const handleViewEstimate = async (estimateId) => {
    try {
      const response = await api.get(`/estimates/${estimateId}`);
      setSelectedEstimate(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load estimate details');
    }
  };

  const handleDuplicateEstimate = async (estimateId) => {
    try {
      await api.post(`/estimates/${estimateId}/duplicate`);
      toast.success('Estimate duplicated as a new draft');
      fetchEstimates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate estimate');
    }
  };

  const handleSendToCustomer = async (estimateId) => {
    try {
      await api.put(`/estimates/${estimateId}/send`);
      toast.success('Estimate marked as Sent');
      fetchEstimates();
      // Open the view modal after marking as sent
      const response = await api.get(`/estimates/${estimateId}`);
      setSelectedEstimate(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send estimate');
    }
  };

  const handleDownloadEstimatePDF = async () => {
    const element = document.getElementById('estimate-pdf-content');

    // Remove scroll/height constraints on all scrollable ancestors so
    // html2canvas captures the full content, not just the visible scroll area
    const constrained = [];
    let node = element.parentElement;
    while (node && node !== document.body) {
      const cs = window.getComputedStyle(node);
      if (cs.overflowY === 'auto' || cs.overflowY === 'scroll' || cs.maxHeight !== 'none') {
        constrained.push({ el: node, overflowY: node.style.overflowY, maxHeight: node.style.maxHeight });
        node.style.overflowY = 'visible';
        node.style.maxHeight = 'none';
      }
      node = node.parentElement;
    }

    try {
      // ── Step 1: measure DOM positions BEFORE html2canvas ─────────────────
      const elementRect = element.getBoundingClientRect();
      const relTop    = (el) => el.getBoundingClientRect().top    - elementRect.top;
      const relBottom = (el) => el.getBoundingClientRect().bottom - elementRect.top;

      // Header = first child div (logo + date box section)
      const headerEl        = element.firstElementChild;
      const headerBottomCss = relBottom(headerEl);

      // Date/quotation box = right child of header — hidden on pages 2+
      const dateBoxEl = headerEl ? headerEl.lastElementChild : null;
      const dateBoxRect = dateBoxEl ? dateBoxEl.getBoundingClientRect() : null;
      const dateBoxCss = dateBoxRect ? {
        left:   dateBoxRect.left   - elementRect.left,
        top:    dateBoxRect.top    - elementRect.top,
        width:  dateBoxRect.width,
        height: dateBoxRect.height,
      } : null;

      // Footer = everything after the table (notes + terms + signature)
      const table        = element.querySelector('table');
      const afterTableEl = table ? table.parentElement.nextElementSibling : null;
      const footerTopCss = afterTableEl ? relTop(afterTableEl) : elementRect.height;

      // Table rows for break-point detection
      const tableRows     = Array.from(element.querySelectorAll('table tr'));
      const rowBottomsCss = tableRows.map(row => relBottom(row));

      // ── Step 2: capture full canvas ───────────────────────────────────────
      const canvas = await html2canvas(element, { scale: 1.5, useCORS: true, logging: false });

      const scaleY  = canvas.height / elementRect.height;
      const scaleX  = canvas.width  / elementRect.width;
      const pxPerMm = canvas.width  / 210;

      // Date box in canvas pixels (used to white it out on pages 2+)
      const dateBoxPx = dateBoxCss ? {
        left:   Math.floor(dateBoxCss.left   * scaleX),
        top:    Math.floor(dateBoxCss.top    * scaleY),
        width:  Math.ceil (dateBoxCss.width  * scaleX) + 4,
        height: Math.ceil (dateBoxCss.height * scaleY) + 4,
      } : null;

      // Convert CSS → canvas pixels
      const headerBottomPx  = Math.round(headerBottomCss * scaleY);
      const footerTopPx     = Math.round(footerTopCss    * scaleY);
      const footerHeightPx  = canvas.height - footerTopPx;
      const contentStartPx  = headerBottomPx;
      const contentHeightPx = footerTopPx - contentStartPx;

      // Row breaks relative to content start
      const rowBreaksPx = rowBottomsCss
        .map(y => Math.round(y * scaleY) - contentStartPx)
        .filter(bp => bp > 0 && bp < contentHeightPx);

      // Pagination ends at the last row's bottom — this prevents the mb-6 margin
      // on the table wrapper from creating a near-empty extra page with just a border line.
      const effectiveContentEnd = rowBreaksPx.length > 0
        ? rowBreaksPx[rowBreaksPx.length - 1]
        : contentHeightPx;

      // ── Step 3: PDF constants ─────────────────────────────────────────────
      const pdf          = new jsPDF('p', 'mm', 'a4');
      const pdfWidth     = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();

      const breakMarginPx = Math.round(5 * pxPerMm); // 5 mm at each break edge
      const fullPagePx    = Math.round(pageHeightMm * pxPerMm);
      // On pages 2+ the header is drawn at 85% height to save vertical space,
      // keeping the logo readable while fitting more content per page
      const headerScale2  = 0.85;

      // ── Step 4: paginate content region ───────────────────────────────────
      // Each slice stores the actual header height used on that page so the
      // renderer and the pagination both agree on available space.
      const slices = [];
      let cur = 0;

      while (cur < effectiveContentEnd) {
        const isFirst   = slices.length === 0;
        const topPad    = isFirst ? 0 : breakMarginPx;
        const hPx       = isFirst ? headerBottomPx : Math.round(headerBottomPx * headerScale2);
        const fixedPx   = hPx + footerHeightPx;
        const maxContent = fullPagePx - fixedPx - topPad - breakMarginPx;
        const maxLast    = fullPagePx - fixedPx - topPad;

        if (cur + maxLast >= effectiveContentEnd) {
          slices.push({ start: cur, end: effectiveContentEnd, topPad, bottomPad: 0, hPx });
          break;
        }

        const ideal   = cur + Math.max(maxContent, 1);
        const valid   = rowBreaksPx.filter(bp => bp > cur && bp <= ideal);
        const breakAt = valid.length > 0 ? valid[valid.length - 1] : Math.max(ideal, cur + 1);

        slices.push({ start: cur, end: breakAt, topPad, bottomPad: breakMarginPx, hPx });
        cur = breakAt;
      }

      // ── Step 5: render each page ──────────────────────────────────────────
      const borderOverlapPx = 3;

      for (let i = 0; i < slices.length; i++) {
        const { start, end, topPad, bottomPad, hPx } = slices[i];
        const overlap  = i > 0 ? Math.min(borderOverlapPx, start) : 0;
        const adjStart = start - overlap;
        const sliceH   = end - adjStart;
        if (sliceH <= 0) continue;

        const pageH      = hPx + topPad + sliceH + bottomPad + footerHeightPx;
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width  = canvas.width;
        pageCanvas.height = pageH;

        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, pageH);

        // 1. Header — source is always full size; destination height is hPx
        //    (scaled down to 85% on pages 2+ to save vertical space)
        ctx.drawImage(canvas, 0, 0,
                      canvas.width, headerBottomPx,
                      0, 0, canvas.width, hPx);

        // On pages 2+, white out the date/quotation box; also scale its Y position
        if (i > 0 && dateBoxPx) {
          const yScale = hPx / headerBottomPx;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(
            dateBoxPx.left,
            Math.floor(dateBoxPx.top  * yScale),
            dateBoxPx.width,
            Math.ceil (dateBoxPx.height * yScale) + 4
          );
        }

        // 2. Content slice — placed after the (scaled) header + top break gap
        ctx.drawImage(canvas, 0, contentStartPx + adjStart,
                      canvas.width, sliceH,
                      0, hPx + topPad, canvas.width, sliceH);

        // 3. Footer — placed after content + bottom break gap
        ctx.drawImage(canvas, 0, footerTopPx,
                      canvas.width, footerHeightPx,
                      0, hPx + topPad + sliceH + bottomPad, canvas.width, footerHeightPx);

        if (i > 0) pdf.addPage();
        pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pdfWidth, pageH / pxPerMm);
      }

      pdf.save(`Estimate-${selectedEstimate.estimate_number}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      constrained.forEach(({ el, overflowY, maxHeight }) => {
        el.style.overflowY = overflowY;
        el.style.maxHeight = maxHeight;
      });
    }
  };

  const handleCopyShareLink = async () => {
    try {
      const res = await api.post(`/estimates/${selectedEstimate.id}/share-token`);
      const url = `${window.location.origin}/estimate/view/${res.data.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch (error) {
      toast.error('Failed to generate share link');
    }
  };

  const handleApproveEstimate = async (estimateId) => {
    try {
      await api.put(`/estimates/${estimateId}/approve`);
      toast.success('Estimate approved successfully', {
        style: { background: '#10b981', color: 'white' }
      });
      fetchEstimates();
    } catch (error) {
      toast.error('Failed to approve estimate', {
        style: { background: '#ef4444', color: 'white' }
      });
    }
  };

  const handleRejectEstimate = async (estimateId) => {
    try {
      await api.put(`/estimates/${estimateId}/reject`);
      toast.success('Estimate rejected successfully', {
        style: { background: '#10b981', color: 'white' }
      });
      fetchEstimates();
    } catch (error) {
      toast.error('Failed to reject estimate', {
        style: { background: '#ef4444', color: 'white' }
      });
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = estimateForm.items.findIndex(i => i._uid === active.id);
      const newIndex = estimateForm.items.findIndex(i => i._uid === over.id);
      setEstimateForm({ ...estimateForm, items: arrayMove(estimateForm.items, oldIndex, newIndex) });
    }
  };

  const addEstimateItem = () => {
    setEstimateForm({
      ...estimateForm,
      items: [...estimateForm.items, { _uid: crypto.randomUUID(), product_id: '', product_name: '', description: '', category_id: '', size: '', unit: 'pcs', quantity: 1, unit_price: 0, display_amounts: true, offcut_size: '', offcut_rate: 0 }]
    });
  };

  const removeEstimateItem = (index) => {
    const newItems = estimateForm.items.filter((_, i) => i !== index);
    setEstimateForm({ ...estimateForm, items: newItems });
  };

  const updateEstimateItem = (index, field, value) => {
    const newItems = [...estimateForm.items];
    newItems[index][field] = value;

    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].description = product.description || '';
        newItems[index].unit_price = product.price;
        newItems[index].category_id = product.category_id || '';
        newItems[index].unit = product.unit || 'pcs';
        newItems[index].offcut_rate = product.offcut_rate || 0;
      }
    }

    setEstimateForm({ ...estimateForm, items: newItems });
  };

  const parseDimension = (str) => {
    if (!str) return 0;
    const parts = String(str).toLowerCase().split(/[x*]/);
    if (parts.length === 2) return (parseFloat(parts[0]) || 0) * (parseFloat(parts[1]) || 0);
    return parseFloat(str) || 0;
  };

  const fmt = (num) => Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getItemTotal = (item) => {
    if (item.unit === 'sqft') {
      const main = parseDimension(item.size) * (item.unit_price || 0);
      const offcut = parseDimension(item.offcut_size) * (item.offcut_rate || 0);
      const qty = parseInt(item.quantity) || 1;
      return (main + offcut) * qty;
    }
    if (item.unit === 'sqin') {
      const qty = parseInt(item.quantity) || 1;
      return parseDimension(item.size) * (item.unit_price || 0) * qty;
    }
    return (item.quantity || 0) * (item.unit_price || 0);
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => {
      if (item.display_amounts !== false) {
        return sum + getItemTotal(item);
      }
      return sum;
    }, 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'converted': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredEstimates = estimates
    .filter(estimate => {
      const term = searchTerm.toLowerCase();
      const customer = customers.find(c => c.id === estimate.customer_id);
      const customerName = (customer?.name || '').toLowerCase();
      return estimate.estimate_number.toLowerCase().includes(term) || customerName.includes(term);
    })
    .sort((a, b) => {
      if (sortByCustomer === 'none') return 0;
      const nameA = (customers.find(c => c.id === a.customer_id)?.name || '').toLowerCase();
      const nameB = (customers.find(c => c.id === b.customer_id)?.name || '').toLowerCase();
      return sortByCustomer === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {showDeleted ? 'Deleted Estimates' : 'Estimates'}
          </h1>
          <div className="flex gap-2">
            {(deletedCount > 0 || showDeleted) && (
              <Button 
                variant={showDeleted ? "default" : "outline"} 
                size="sm" 
                onClick={() => {
                  if (showDeleted) {
                    setSearchParams({});
                  } else {
                    setSearchParams({ view: 'deleted' });
                  }
                }}
                title={showDeleted ? "View Active Estimates" : `View Deleted Estimates (${deletedCount})`}
              >
                <Archive className="w-4 h-4" />
              </Button>
            )}
            <Button onClick={() => setCreateDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create Estimates
            </Button>
          </div>
        </div>

        {estimates.length >= 5 && (
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search by estimate number or customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
            <Button
              variant={sortByCustomer !== 'none' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortByCustomer(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none')}
              title={sortByCustomer === 'asc' ? 'Sorted A–Z (click for Z–A)' : sortByCustomer === 'desc' ? 'Sorted Z–A (click to reset)' : 'Sort by customer name'}
              className="flex items-center gap-1 whitespace-nowrap"
            >
              {sortByCustomer === 'asc' ? <ArrowUp className="w-4 h-4" /> : sortByCustomer === 'desc' ? <ArrowDown className="w-4 h-4" /> : <ArrowUpDown className="w-4 h-4" />}
              {sortByCustomer === 'asc' ? 'Customer A–Z' : sortByCustomer === 'desc' ? 'Customer Z–A' : 'Sort by Customer'}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {filteredEstimates.map((estimate) => (
            <Card key={estimate.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <h3 className="font-bold text-lg">{estimate.estimate_number}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(estimate.status)}`}>
                        {estimate.status.toUpperCase()}
                      </span>
                      {estimate.approval_status === 'approved' && (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700">
                          APPROVED
                        </span>
                      )}
                      {estimate.approval_status === 'rejected' && (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700">
                          REJECTED
                        </span>
                      )}
                      {estimate.approval_status === 'pending' && (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">
                          PENDING APPROVAL
                        </span>
                      )}
                      {estimate.production_notes && (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-700" title={estimate.production_notes}>
                          📋 PRODUCTION NOTES
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Customer</p>
                        <p className="font-semibold">{getCustomerName(estimate.customer_id)}</p>
                        {estimate.subject && (
                          <p className="text-xs text-gray-600 mt-1">{estimate.subject}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-semibold">{estimate.estimate_date}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Valid Until</p>
                        <p className="font-semibold">{estimate.valid_until || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Total</p>
                        <p className="font-semibold text-green-600">Rs {estimate.total.toLocaleString()}</p>
                      </div>
                    </div>
                    {estimate.production_notes && (
                      <div className="mt-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                        <span className="font-semibold">Production Notes: </span>
                        {estimate.production_notes.length > 120
                          ? estimate.production_notes.slice(0, 120) + '…'
                          : estimate.production_notes}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {showDeleted ? (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleRestoreEstimate(estimate.id)}
                        >
                          Restore
                        </Button>
                        <div className="text-xs text-gray-500 flex flex-col items-end">
                          <span>Deleted: {estimate.deleted_at ? new Date(estimate.deleted_at).toLocaleDateString() : ''}</span>
                          {estimate.deleted_by && <span>By: {estimate.deleted_by}</span>}
                        </div>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewEstimate(estimate.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>

                        {/* Send to Customer - only for draft estimates */}
                        {estimate.status === 'draft' && (
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleSendToCustomer(estimate.id)}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Send to Customer
                          </Button>
                        )}

                        {/* Approve/Reject buttons - only for admin/manager/accountant */}
                        {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'accountant') && estimate.approval_status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveEstimate(estimate.id)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRejectEstimate(estimate.id)}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}

                        {/* Re-approve button for rejected estimates */}
                        {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'accountant') && estimate.approval_status === 'rejected' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApproveEstimate(estimate.id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        )}

                        {/* Reject button for approved estimates - admin can change back to rejected */}
                        {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'accountant') && estimate.approval_status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRejectEstimate(estimate.id)}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        )}

                        {/* Convert to Invoice button - employees can only convert approved estimates */}
                        {estimate.status !== 'converted' && (
                          <>
                            {((user?.role === 'admin' || user?.role === 'manager' || user?.role === 'accountant') ||
                              (estimate.approval_status === 'approved')) && (
                              <Button
                                size="sm"
                                onClick={() => handleConvertToInvoice(estimate.id)}
                              >
                                <ArrowRight className="w-4 h-4 mr-1" />
                                Convert to Invoice
                              </Button>
                            )}
                            {/* Edit button - all users can edit estimates */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditEstimate(estimate)}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            {/* Duplicate button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDuplicateEstimate(estimate.id)}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Duplicate
                            </Button>
                          </>
                        )}
                        {/* Delete button - all users can delete estimates */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteEstimate(estimate.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredEstimates.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              {searchTerm ? 'No estimates found matching your search' : 'No estimates yet. Click "Create Estimate" to get started.'}
            </CardContent>
          </Card>
        )}

        {/* Create Estimate Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEstimate ? 'Edit Estimate' : 'Create New Estimate'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateEstimate} className="space-y-4">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                  <Select 
                    value={estimateForm.customer_id} 
                    onValueChange={(value) => {
                      if (value === 'add_new') {
                        setAddCustomerDialogOpen(true);
                      } else {
                        setEstimateForm({ ...estimateForm, customer_id: value });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Customer *" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add_new" className="text-blue-600 font-semibold">
                        + Add New Customer
                      </SelectItem>
                      {customers.map(customer => (
                        <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <label className="block text-xs font-medium mb-1">Estimate Date *</label>
                  <Input
                    type="date"
                    value={estimateForm.estimate_date}
                    onChange={(e) => setEstimateForm({ ...estimateForm, estimate_date: e.target.value })}
                    required
                  />
                </div>
                <div className="col-span-6">
                  <label className="block text-xs font-medium mb-1">Valid Until</label>
                  <Input
                    type="date"
                    value={estimateForm.valid_until}
                    onChange={(e) => setEstimateForm({ ...estimateForm, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Subject</label>
                <Input
                  type="text"
                  value={estimateForm.subject}
                  onChange={(e) => setEstimateForm({ ...estimateForm, subject: e.target.value })}
                  placeholder="e.g., Sticker Print & Cut"
                />
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Estimate Items</h3>
                  <Button type="button" size="sm" onClick={addEstimateItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>

                {/* Column Headers */}
                <div className="grid grid-cols-12 gap-2 mb-2 px-1">
                  <div className="col-span-1"></div>
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Product</div>
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Name</div>
                  <div className="col-span-1 text-xs font-semibold text-gray-600">Size</div>
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Qty</div>
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Price</div>
                  <div className="col-span-1 text-xs font-semibold text-gray-600 text-center">Total</div>
                  <div className="col-span-1 text-xs font-semibold text-gray-600 text-center">Add</div>
                </div>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={estimateForm.items.map(i => i._uid)} strategy={verticalListSortingStrategy}>
                    {estimateForm.items.map((item, index) => (
                      <SortableItem key={item._uid} id={item._uid}>
                        {(dragListeners) => (
                          <div className="grid grid-cols-12 gap-2 mb-3 items-center">
                            <div className="col-span-1 flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" {...dragListeners}>
                              <GripVertical className="w-4 h-4" />
                            </div>
                            <div className="col-span-2">
                              <Select
                                value={item.product_id || "custom"}
                                onValueChange={(value) => updateEstimateItem(index, 'product_id', value === "custom" ? "" : value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Product" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="custom">Custom Item</SelectItem>
                                  {products.map(product => (
                                    <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Textarea
                                value={item.product_name}
                                onChange={(e) => updateEstimateItem(index, 'product_name', e.target.value)}
                                placeholder="Name *"
                                required
                                rows={1}
                                className="min-h-0 resize"
                              />
                            </div>
                            <div className="col-span-1">
                              <Input
                                value={item.size}
                                onChange={(e) => updateEstimateItem(index, 'size', e.target.value)}
                                placeholder="Size"
                              />
                              {item.unit === 'sqft' && (
                                <Input
                                  value={item.offcut_size}
                                  onChange={(e) => updateEstimateItem(index, 'offcut_size', e.target.value)}
                                  placeholder="Offcut Size"
                                  className="mt-1 text-xs"
                                />
                              )}
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                step="1"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateEstimateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                                placeholder="Qty *"
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateEstimateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                placeholder="Price *"
                                required
                              />
                            </div>
                            <div className="col-span-1">
                              <p className="text-sm font-semibold text-center">Rs {fmt(getItemTotal(item))}</p>
                            </div>
                            <div className="col-span-1 flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={item.display_amounts !== false}
                                onChange={(e) => updateEstimateItem(index, 'display_amounts', e.target.checked)}
                                className="w-4 h-4 cursor-pointer"
                                title="Include this item's total in grand total"
                              />
                            </div>
                            <div className="col-span-1 flex gap-1">
                              {!item.product_id && item.product_name && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => handleAddItemToProducts(item)}
                                  title="Add to Products"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              )}
                              {estimateForm.items.length > 1 && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => removeEstimateItem(index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </SortableItem>
                    ))}
                  </SortableContext>
                </DndContext>

                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-end">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Subtotal</p>
                      <p className="text-2xl font-bold text-green-600">Rs {fmt(calculateTotal(estimateForm.items))}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Discount Section */}
              <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 rounded border">
                <div className="col-span-12">
                  <label className="block text-sm font-medium mb-2">Discount (Optional)</label>
                </div>
                <div className="col-span-6">
                  <label className="block text-xs text-gray-600 mb-1">Discount Type</label>
                  <Select
                    value={estimateForm.discount_type}
                    onValueChange={(value) => setEstimateForm({ ...estimateForm, discount_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="amount">Fixed Amount (Rs)</SelectItem>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-6">
                  <label className="block text-xs text-gray-600 mb-1">
                    Discount Value {estimateForm.discount_type === 'percentage' ? '(%)' : '(Rs)'}
                  </label>
                  <Input
                    type="number"
                    value={estimateForm.discount}
                    onChange={(e) => setEstimateForm({ ...estimateForm, discount: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                    step={estimateForm.discount_type === 'percentage' ? '0.01' : '1'}
                  />
                </div>
                <div className="col-span-12 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">Rs {fmt(calculateTotal(estimateForm.items))}</span>
                  </div>
                  {estimateForm.discount > 0 && (
                    <div className="flex justify-between py-1 text-red-600">
                      <span>Discount:</span>
                      <span>
                        - Rs {fmt(estimateForm.discount_type === 'percentage'
                          ? calculateTotal(estimateForm.items) * (estimateForm.discount / 100)
                          : estimateForm.discount
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-1 text-lg font-bold border-t mt-1 pt-1">
                    <span>Total:</span>
                    <span className="text-green-600">
                      Rs {fmt(
                        calculateTotal(estimateForm.items) -
                        (estimateForm.discount_type === 'percentage'
                          ? calculateTotal(estimateForm.items) * (estimateForm.discount / 100)
                          : estimateForm.discount
                        )
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Textarea
                  value={estimateForm.notes}
                  onChange={(e) => setEstimateForm({ ...estimateForm, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Production notes <span className="text-xs text-gray-400 font-normal">(internal only, not shown to customer)</span>
                </label>
                <Textarea
                  value={estimateForm.production_notes}
                  onChange={(e) => setEstimateForm({...estimateForm, production_notes: e.target.value})}
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Bank Account for Payment</label>
                <Select
                  value={estimateForm.bank_account}
                  onValueChange={(value) => setEstimateForm({ ...estimateForm, bank_account: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ndb">NDB Bank — Ekma Digital Solutions Pvt Ltd (101000707296)</SelectItem>
                    <SelectItem value="commercial">Commercial Bank — Ekma Digital Solutions (Pvt) Ltd (1001073055)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                <input
                  type="checkbox"
                  id="display-total-amounts"
                  checked={estimateForm.display_total_amounts !== false}
                  onChange={(e) => setEstimateForm({ ...estimateForm, display_total_amounts: e.target.checked })}
                  className="w-4 h-4 cursor-pointer"
                />
                <label htmlFor="display-total-amounts" className="text-sm font-medium cursor-pointer">
                  Display Total Amounts (uncheck to hide all unit prices and totals in estimate view)
                </label>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                <input
                  type="checkbox"
                  id="display-grand-total"
                  checked={estimateForm.display_grand_total !== false}
                  onChange={(e) => setEstimateForm({ ...estimateForm, display_grand_total: e.target.checked })}
                  className="w-4 h-4 cursor-pointer"
                />
                <label htmlFor="display-grand-total" className="text-sm font-medium cursor-pointer">
                  Display Grand Total (uncheck to hide the grand total row in estimate view)
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Estimate</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Customer Dialog (Inline) */}
        <Dialog open={addCustomerDialogOpen} onOpenChange={setAddCustomerDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddNewCustomer} className="space-y-4">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <label className="block text-sm font-medium mb-1">Customer Name *</label>
                  <Input
                    value={newCustomerData.name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                    placeholder="Enter customer name"
                    required
                  />
                </div>
                <div className="col-span-6">
                  <label className="block text-sm font-medium mb-1">Company Name</label>
                  <Input
                    value={newCustomerData.company_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, company_name: e.target.value })}
                    placeholder="Enter company name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    value={newCustomerData.phone}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit phone"
                    maxLength={10}
                  />
                </div>
                <div className="col-span-6">
                  <label className="block text-sm font-medium mb-1">WhatsApp</label>
                  <Input
                    value={newCustomerData.whatsapp}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, whatsapp: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit WhatsApp"
                    maxLength={10}
                  />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={newCustomerData.email}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                    placeholder="customer@example.com"
                  />
                </div>
                <div className="col-span-6">
                  <label className="block text-sm font-medium mb-1">City</label>
                  <Input
                    value={newCustomerData.city}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, city: e.target.value })}
                    placeholder="Enter city"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <Textarea
                  value={newCustomerData.address}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                  placeholder="Enter full address"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAddCustomerDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Add Customer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Product Dialog */}
        <Dialog open={addProductDialogOpen} onOpenChange={setAddProductDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Product</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddNewProduct} className="space-y-4">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-8">
                  <label className="block text-sm font-medium mb-1">Product Name *</label>
                  <Input
                    value={newProductData.name}
                    onChange={(e) => setNewProductData({ ...newProductData, name: e.target.value })}
                    placeholder="Enter product name"
                    required
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <Select
                    value={newProductData.category_id || "none"}
                    onValueChange={(value) => setNewProductData({ ...newProductData, category_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {categories.map(category => (
                        <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Product Description</label>
                <Textarea
                  value={newProductData.description}
                  onChange={(e) => setNewProductData({ ...newProductData, description: e.target.value })}
                  placeholder="Enter product description"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">Price (Rs) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newProductData.price}
                    onChange={(e) => setNewProductData({ ...newProductData, price: e.target.value })}
                    placeholder="Enter price"
                    required
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">Unit *</label>
                  <Select
                    value={newProductData.unit}
                    onValueChange={(value) => setNewProductData({ ...newProductData, unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">Pieces</SelectItem>
                      <SelectItem value="kg">Kilograms</SelectItem>
                      <SelectItem value="hrs">Hours</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="sqft">per sq.ft.</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium mb-1">Stock Qty</label>
                  <Input
                    type="number"
                    value={newProductData.stock_quantity}
                    onChange={(e) => setNewProductData({ ...newProductData, stock_quantity: e.target.value })}
                    placeholder="Stock quantity"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAddProductDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Add Product</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Estimate Dialog */}
        {selectedEstimate && (
          <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>Estimate Preview</DialogTitle>
                  <div className="flex items-center gap-2 mr-16">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyShareLink}
                      className="flex items-center gap-2"
                    >
                      <Link2 className="w-4 h-4" />
                      Copy Share Link
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleDownloadEstimatePDF}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              <div id="estimate-pdf-content" className="bg-white px-12 py-8">
                {/* Header Section */}
                <div className="relative mb-4 flex justify-between items-start">
                  {/* Left - Header Image (Logo and Contact Details) */}
                  <div>
                    <img
                      src="/estimate-header.jpg"
                      alt="Estimate Header"
                      className="h-auto"
                      style={{ maxWidth: '600px' }}
                    />
                  </div>

                  {/* Right - Date and Quotation Number Box */}
                  <div className="border-2 border-green-600 rounded-lg p-3 bg-white min-w-[180px]">
                    <div className="mb-2">
                      <p className="text-xs font-semibold">Date:</p>
                      <p className="text-sm">{selectedEstimate.estimate_date}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold">Quotation No:</p>
                      <p className="text-sm">{selectedEstimate.estimate_number}</p>
                    </div>
                  </div>
                </div>

                {/* Quotation Title */}
                <div className="mb-4 mt-2">
                  <h2 className="text-2xl font-bold">Quotation</h2>
                </div>

                {/* Customer Name and Subject */}
                <div className="mb-4">
                  <p className="text-2xl font-bold mb-1">{selectedEstimate.customer?.name}</p>
                  {selectedEstimate.subject && (
                    <p className="text-base text-gray-800 mb-1">{selectedEstimate.subject}</p>
                  )}
                </div>

                {/* Items Table */}
                <div className="mb-6">
                  <table className="w-full border-2 border-black rounded-lg" style={{ borderCollapse: 'separate', borderSpacing: 0, borderRadius: '8px', overflow: 'hidden' }}>
                    <thead>
                      <tr className="bg-white">
                        <th className="border border-black text-left p-3 font-semibold">Item</th>
                        <th className="border border-black text-center p-3 font-semibold">Size</th>
                        <th className="border border-black text-center p-3 font-semibold">Qty.</th>
                        <th className="border border-black text-center p-3 font-semibold">Unit Price</th>
                        <th className="border border-black text-right p-3 font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEstimate.items
                        .map((item, index) => (
                        <tr key={index}>
                          <td className="border border-black p-3">
                            <div>
                              <p className="font-semibold mb-1">{item.product_name}</p>
                              {item.description && (
                                <p className="text-xs italic text-gray-600">{item.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="border border-black text-center p-3">{item.size || ''}</td>
                          <td className="border border-black text-center p-3">{item.quantity}</td>
                          <td className="border border-black text-center p-3">
                            {item.display_amounts !== false && selectedEstimate.display_total_amounts !== false ? `Rs.${fmt(item.unit_price)}` : ''}
                          </td>
                          <td className="border border-black text-right p-3">
                            {item.display_amounts !== false && selectedEstimate.display_total_amounts !== false ? `Rs.${fmt(item.total)}` : ''}
                          </td>
                        </tr>
                      ))}
                      {/* Subtotal Row */}
                      {selectedEstimate.discount > 0 && (
                        <tr className="bg-white">
                          <td colSpan="4" className="border border-black text-right p-3">Subtotal</td>
                          <td className="border border-black text-right p-3">
                            {selectedEstimate.display_total_amounts !== false ? `Rs.${fmt(selectedEstimate.subtotal)}` : ''}
                          </td>
                        </tr>
                      )}
                      {/* Discount Row */}
                      {selectedEstimate.discount > 0 && selectedEstimate.display_total_amounts !== false && (
                        <tr className="bg-white">
                          <td colSpan="4" className="border border-black text-right p-3">
                            Discount {selectedEstimate.discount_type === 'percentage' ? `(${selectedEstimate.discount}%)` : ''}
                          </td>
                          <td className="border border-black text-right p-3">
                            - Rs.{fmt(selectedEstimate.discount_type === 'percentage'
                              ? selectedEstimate.subtotal * (selectedEstimate.discount / 100)
                              : selectedEstimate.discount
                            )}
                          </td>
                        </tr>
                      )}
                      {/* Total Row */}
                      {selectedEstimate.display_grand_total !== false && (
                        <tr className="bg-white">
                          <td colSpan="4" className="border border-black text-right p-3 font-bold">Total</td>
                          <td className="border border-black text-right p-3 font-bold">
                            {selectedEstimate.display_total_amounts !== false ? `Rs.${fmt(selectedEstimate.total)}` : ''}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Notes Section */}
                {selectedEstimate.notes && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-700">{selectedEstimate.notes}</p>
                  </div>
                )}


                {/* Terms and Conditions */}
                <div className="mb-6 text-sm">
                  <p className="mb-2">
                    An advance of 70% (Seventy Percent) is requested at the time of placing of the order. This quotation is valid for 30 Days.
                  </p>
                  <p>
                    The online payment to be made to "
                    <strong>
                      {selectedEstimate.bank_account === 'commercial'
                        ? 'Bank Name - Commercial Bank | Acc.Name - Ekma Digital Solutions (Private) Limited | Acc. Number- 1001073055'
                        : 'Bank Name - NDB Bank | Acc.Name - Ekma Digital Solutions Pvt Ltd | Acc. Number- 101000707296 | Branch - Boralesgamuwa'}
                    </strong>"
                  </p>
                </div>

                {/* Footer Section */}
                <div className="space-y-1">
                  <p className="text-base">Thank you</p>
                  <p className="text-base">Your truly,</p>
                  <img
                    src="/signature.png"
                    alt="Signature"
                    className="h-16 w-auto my-2"
                  />
                  <p className="text-base font-semibold">S. G. Jamitha</p>
                  <p className="text-base">Ekma Digital Solutions (Private) Limited.</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
